import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useSnackbarStore } from '@certara/common-react-components';

interface MoveApiResponse {
  IsSuccess: boolean;
  ErrorMessage?: string;
}

export interface FileMoveProgress {
  itemId: string;
  itemName: string;
  progress: number; // 0-100
  status: 'pending' | 'moving' | 'completed' | 'failed';
  error?: string;
}

export interface UseFileMoveOptions {
  companyId?: string;
  sourcePath?: string;
  onProgress?: (progress: Map<string, FileMoveProgress>) => void;
}

/**
 * Hook for managing file move operations
 * Handles moving files/folders between locations
 */
export function useFileMove(options: UseFileMoveOptions = {}) {
  const { companyId, sourcePath = '/', onProgress } = options;
  const [moveProgress, setMoveProgress] = useState<Map<string, FileMoveProgress>>(
    new Map()
  );
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  // Update progress state
  const updateProgress = (itemId: string, updates: Partial<FileMoveProgress>) => {
    setMoveProgress((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(itemId) || {
        itemId,
        itemName: '',
        progress: 0,
        status: 'pending' as const,
      };
      newMap.set(itemId, { ...current, ...updates });
      onProgress?.(newMap);
      return newMap;
    });
  };

  const moveMutation = useMutation({
    mutationFn: async (params: {
      itemIds: string[];
      itemNames: string[];
      targetPath: string;
    }) => {
      if (!companyId) {
        throw new Error('Company ID is required for moves');
      }

      const { itemIds, itemNames, targetPath } = params;

      const results = await Promise.allSettled(
        itemIds.map((itemId, idx) =>
          (async () => {
            const itemName = itemNames[idx];
            try {
              updateProgress(itemId, {
                status: 'moving',
                itemName,
                progress: 10,
              });

              const response = await apiClient.post<MoveApiResponse>('api/storage/Move', {
                companyId,
                itemId,
                itemName,
                sourcePath,
                targetPath,
              });

              if (!response?.IsSuccess) {
                updateProgress(itemId, {
                  status: 'failed',
                  error: response?.ErrorMessage || 'Move failed',
                  progress: 0,
                });
                throw new Error(response?.ErrorMessage || 'Move failed');
              }

              updateProgress(itemId, {
                status: 'completed',
                progress: 100,
              });

              return { itemId, success: true };
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Move failed';
              updateProgress(itemId, {
                status: 'failed',
                error: errorMessage,
                progress: 0,
              });
              return { itemId, success: false, error: errorMessage };
            }
          })()
        )
      );

      return results;
    },

    onSuccess: (results) => {
      const successful = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - successful;

      if (failed > 0) {
        addSnackbar(`Moved ${successful} of ${results.length} items`, 'warning');
      } else {
        addSnackbar(`Successfully moved ${successful} items`, 'success');
      }

      // Invalidate grid data to refresh
      queryClient.invalidateQueries({ queryKey: ['gridData'] });
    },

    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      addSnackbar('Move failed', 'error', message, true);
    },
  });

  const moveItems = async (
    itemIds: string[],
    itemNames: string[],
    targetPath: string
  ) => {
    if (!itemIds.length) return;

    // Initialize progress for all items
    itemIds.forEach((id, idx) => {
      updateProgress(id, {
        status: 'pending',
        progress: 0,
        itemName: itemNames[idx],
      });
    });

    await moveMutation.mutateAsync({
      itemIds,
      itemNames,
      targetPath,
    });
  };

  const clearProgress = () => {
    setMoveProgress(new Map());
  };

  return {
    moveItems,
    moveProgress,
    isMoving: moveMutation.isPending,
    clearProgress,
  };
}
